/**
 * buildBedrockColumns —— 纯翻译器：引擎的 `ColumnMeta[]` → @bedrock/table 的列定义。
 *
 * 【为何不按 doc/subArray/custom 三个分支再拆三个文件】三分支共享同一批闭包
 * （`rules` / `forceValidate` / `ruleForm` / 6 个写回 handler / 一批渲染件），
 * 拆开要传十几个参数，反而更难读。故保持单文件三分支。
 *
 * ⚠️ **本文件是纯函数，不含 useMemo。** 调用方（`ProTableListForm.tsx`）负责
 * `useMemo` 与**逐字冻结的依赖数组** —— 见 `scripts/check-deps-array.mjs`。
 * 把依赖数组搬进本文件会破坏该门禁的意义。
 */
import React from 'react';
import type { AddGroupFormColumn } from '@mpc/common/src/components/ProTablePlusForm/addGroupTable';
import { BatchEditCellTitle, ColumnFixedButtonLayer, groupTitle, subTitle } from './columnHeaderWidgets';
import { CellField, renderOpColCell, renderSubColCell } from './renderCells';
import { renderDragColCell } from './dragCell';
import { subHasAddRow, subRowVisible } from './rowMetrics';
import type {
  ChildBase,
  ChildId,
  ColumnMeta,
  CustomColumnMeta,
  DocFieldMeta,
  FieldRules,
  RowBase,
  RowId,
} from './types';

/** 子表删除/操作占位宽（px） */
export const OP_W = 60;
/** 子表拖拽手柄列占位宽（px） */
export const DRAG_W = 32;

/** 主表单元格顶部对齐 + 上下 8px 内边距（正常列无子项，内容靠上且上下留白） */
export const topAlignCell = () => ({
  style: {
    verticalAlign: 'top',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    paddingTop: 8,
    paddingBottom: 8,
  },
});
/** 子表宿主 cell：顶部对齐 + 删除 cell 上下左右 padding（子项列内容占满 cell，避免最后一行被 padding 顶出裁切） */
export const subCellAlign = () => ({
  style: { verticalAlign: 'top', alignItems: 'flex-start', justifyContent: 'flex-start', paddingLeft: 0, paddingRight: 0, paddingTop: 0, paddingBottom: 0 },
});

/** 翻译器的全部输入（闭包） */
export interface BuildBedrockColumnsParams<TRow extends RowBase, TChild extends ChildBase> {
  columns: ColumnMeta<TRow, TChild>[];
  rules: FieldRules;
  /** 外部全量校验态：进入列定义供 CellField / renderSubColCell 透传，决定是否全表标红 */
  forceValidate: boolean;
  /** 表单实例：**有意不进 useMemo 依赖数组**（identity 稳定），见 dev.spec 陷阱 8 */
  ruleForm: unknown;
  handleBatchApply: (field: string, value: string, mode: 'keep' | 'cover') => void;
  updateRow: (id: RowId, patch: Partial<TRow>) => void;
  patchItem: (rowId: RowId, subKey: string, childId: ChildId, p: Record<string, unknown>) => void;
  requestAddItem: (row: TRow, subKey: string) => void;
  removeItem: (rowId: RowId, subKey: string, childId: ChildId) => void;
  reorderItems: (rowId: RowId, subKey: string, fromIndex: number, toIndex: number) => void;
}

export function buildBedrockColumns<TRow extends RowBase, TChild extends ChildBase>({
  columns, rules, forceValidate, ruleForm,
  handleBatchApply, updateRow, patchItem, requestAddItem, removeItem, reorderItems,
}: BuildBedrockColumnsParams<TRow, TChild>): AddGroupFormColumn[] {
  const out: AddGroupFormColumn[] = [];
  for (const c of columns) {
    if (c.kind === 'doc') {
      const d: DocFieldMeta<TRow> = c.doc;
      out.push({
        dataIndex: d.field,
        // 固定按钮层与标题并列渲染：其 absolute 包含块即 th 的 padding box（见 ColumnFixedButtonLayer）
        title: (
          <>
            <ColumnFixedButtonLayer btn={d.fixedButton} />
            <BatchEditCellTitle title={d.title} field={d.field} meta={{ type: d.type, options: d.options, required: d.required }} onBatchApply={handleBatchApply} />
          </>
        ),
        width: d.width,
        resizable: d.resizable,
        onCell: topAlignCell,
        render: (_: unknown, row: TRow) => (
          <CellField rowId={row.id} value={row[d.field]} field={d.field} type={d.type} options={d.options} required={d.required} rules={rules} onUpdate={updateRow} forceValidate={forceValidate} form={ruleForm} />
        ),
      } as AddGroupFormColumn);
    } else if (c.kind === 'subArray') {
      const sub = c.sub;
      /**
       * 「子列动作」的落点列：由 `sub.rowActionColumn` 决定（默认**第二子列**），
       * `'first'` 时落第一子列（与「+添加{emptyLabel}」同 cell、同行并排）。
       * 无论哪种配置，子列不足两个时一律回退 `columns[0]`（否则动作会没处落）。
       *
       * ⚠️ 用 `sub.columns` 而非渲染后的 children 计算下标 —— 拖拽子列（`${sub.key}Drag`）由本文件
       * 前置插入、不在 `sub.columns` 里，与 `showAdd` 的 `columns[0]` 口径保持一致。
       */
      const rowActionCol = sub.renderRowAction
        ? (sub.rowActionColumn === 'first' ? sub.columns[0] : sub.columns[1] ?? sub.columns[0])
        : undefined;
      out.push({
        key: sub.key,
        dataIndex: sub.key,
        title: groupTitle(sub.title, sub.required),
        children: [
          ...(sub.sortable ? [{
            key: `${sub.key}Drag`,
            dataIndex: `${sub.key}Drag`,
            title: '',
            width: DRAG_W,
            onCell: subCellAlign,
            render: (_: unknown, row: TRow) => (subRowVisible(sub, row)
              ? renderDragColCell(row.id, sub.key, sub.getItems(row), reorderItems, sub.getDragLabel)
              : null),
          }] : []),
          ...sub.columns.map((col) => ({
            key: col.field,
            dataIndex: col.field,
            title: subTitle(col.title, col.required),
            width: col.width,
            resizable: col.resizable,
            onCell: subCellAlign,
            render: (_: unknown, row: TRow) => {
              if (!subRowVisible(sub, row)) return null;
              const items = sub.getItems(row);
              // 整体必填却一条子项都没有：格内没有输入控件可标红（只有这个添加入口），
              // 故错误态落在按钮上。判据与 findFirstDoc 的空子项校验同源。
              // 仅在「已触发过全量校验」后显示，避免用户刚打开表单就满屏红字。
              const addError = forceValidate && sub.required && items.length === 0
                ? `${sub.title}不能为空，请点击「+ 添加${sub.emptyLabel}」`
                : undefined;
              return renderSubColCell(row.id, sub.key, col, patchItem, items, sub.emptyLabel, col === sub.columns[0] && subHasAddRow(sub), () => requestAddItem(row, sub.key), {
                forceValidate,
                addError,
                form: ruleForm,
                // 与「+添加{emptyLabel}」**同门控**（`subHasAddRow`）：`disableAdd` 为真时一并不渲染，
                // 故不额外占行（见 SubArraySpec.renderRowAction 的行高说明）。
                rowAction: sub.renderRowAction && col === rowActionCol && subHasAddRow(sub) ? sub.renderRowAction(row) : undefined,
              });
            },
          })),
          ...(sub.showDelete === false ? [] : [{
            key: `${sub.key}Op`,
            dataIndex: `${sub.key}Op`,
            title: subTitle('操作'),
            width: OP_W,
            onCell: subCellAlign,
            render: (_: unknown, row: TRow) => {
              if (!subRowVisible(sub, row)) return null;
              const items = sub.getItems(row);
              // 禁用删除：显式 disableDelete，或子项数已达下限 minItems
              // （对齐面板 KPITable 的 ableDelete —— 必填指标至少保留 1 条）
              const deleteDisabled = !!sub.disableDelete || items.length <= (sub.minItems ?? 0);
              return renderOpColCell(items, (childId) => removeItem(row.id, sub.key, childId), deleteDisabled);
            },
          }]),
        ],
      } as AddGroupFormColumn);
    } else if (c.kind === 'custom') {
      const cu: CustomColumnMeta<TRow> = c.custom;
      out.push({
        key: cu.key,
        dataIndex: cu.key,
        // 有 batchEdit 时表头用 BatchEditCellTitle（批量编辑 popover），否则纯文本。
        // custom 列批量写回由 batchEdit.apply 接管（引擎忽略 field 参数，只透传 value/mode）。
        // 只要有「批量编辑 meta」或「标题旁挂件（如钉在左侧）」其一的列，就走标题容器渲染。
        // ⚠️ 不能只判 batchEdit：那会让「无批量编辑但有 titleExtra」的列丢掉挂件
        // （曾发生：级联列去掉 batchEdit 后「钉在左侧」按钮一并消失）。
        // 固定按钮层与标题并列渲染：其 absolute 包含块即 th 的 padding box（见 ColumnFixedButtonLayer）
        title: (
          <>
            <ColumnFixedButtonLayer btn={cu.fixedButton} />
            {(cu.batchEdit || cu.titleExtra) ? (
              <BatchEditCellTitle title={cu.title} field={cu.key} meta={cu.batchEdit} extra={cu.titleExtra} onBatchApply={cu.batchEdit?.apply ? (f: string, value: string, mode: 'keep' | 'cover') => cu.batchEdit!.apply!(value, mode) : handleBatchApply}/>
            ) : cu.title}
          </>
        ),
        width: cu.width,
        resizable: cu.resizable,
        fixed: cu.fixed,
        onCell: topAlignCell,
        render: (_: unknown, row: TRow) => cu.render(row),
      } as AddGroupFormColumn);
    }
  }
  return out;
}
